import { type SupplierIntegrationEntry } from '@choco-admin/types';

import { type SupplierFormData } from '../components/SupplierForm/types';

import {
  hasEditedEmailNotifications,
  hasEditedNotifications,
  type DirtyFormFields,
} from './buildSupplierUpdate';

type IntegrationEntryInput = {
  email?: {
    type: 'email';
    emailAddress?: string;
    // Always sent, never omitted: an absent list is refilled server-side with
    // `emailAddress` (see parseEmailList).
    orderEmails: string[];
    messageEmails: string[];
    complaintEmails: string[];
    pdf: boolean;
    xlsx: boolean;
  };
  whatsApp?: { type: 'whatsApp'; phone: string };
  sms?: { type: 'sms'; phone: string };
  fax?: { type: 'fax'; phone: string };
};

/**
 * Parse a comma-separated email string into an array of trimmed, non-empty
 * addresses — mirroring how the form's text fields are seeded from arrays in
 * extractNotificationDefaults.
 */
/**
 * Splits a comma-separated field into addresses. Empty means an empty list, not
 * an absent one.
 *
 * The distinction is the whole point. `supplierService.mapIntegration` does
 * `orderEmails ?? [emailAddress]`, so an omitted list is refilled with the
 * deprecated single address: clearing the order email would quietly redirect
 * orders to the distributor's bot address and report success. An explicit `[]`
 * is left alone.
 */
export const parseEmailList = (raw: string): string[] =>
  raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

/**
 * Build the integration array for the supplierUpdate mutation from form data.
 *
 * - When the form's notification fields are all empty AND no existing
 *   integration data is present, returns undefined so we don't touch the
 *   server-side integration array at all.
 * - When they are all empty because the person editing just cleared them,
 *   returns the replacement array (`[]` when nothing else is stored). An
 *   omitted key is a merge, so returning undefined here would leave the
 *   destination they deleted still receiving orders.
 * - When existing integration data IS present (edit mode), always returns a
 *   complete array so that unexposed entries (fax, multiple objects) and
 *   existing email pdf/xlsx flags are preserved.
 * - Form values override the matching typed entry from the existing array;
 *   entries whose type is not exposed by the form (fax) are carried through
 *   verbatim.
 */
export const buildIntegrationPayload = (
  data: SupplierFormData,
  existingIntegration?: SupplierIntegrationEntry[] | null,
  dirtyFields: DirtyFormFields = {}
): IntegrationEntryInput[] | undefined => {
  const emailFieldsEdited = hasEditedEmailNotifications(dirtyFields);
  const orderEmails = data.notificationOrderEmails.trim();
  const messageEmails = data.notificationSystemAlertEmails.trim();
  const complaintEmails = data.notificationComplaintEmails.trim();
  const whatsappPhone = data.notificationWhatsappNumber.trim();
  const smsPhone = data.notificationSmsNumber.trim();

  const hasAnyFormValue =
    orderEmails ||
    messageEmails ||
    complaintEmails ||
    whatsappPhone ||
    smsPhone;

  // In create mode with no existing data and no values entered, omit the field.
  if (!hasAnyFormValue && !existingIntegration?.length) {
    return undefined;
  }

  // Locate the existing email entry (to carry over pdf/xlsx flags).
  const existingEmail = (existingIntegration ?? []).find(
    (
      e
    ): e is Extract<
      SupplierIntegrationEntry,
      { __typename: 'IntegrationEmailOption' }
    > => e.__typename === 'IntegrationEmailOption'
  );

  // Entries the form does not expose have to be sent back verbatim, because
  // `supplierUpdate` replaces the whole array rather than merging it. Fax is
  // the only one: it is deprecated server-side and has no field on any tab, so
  // without this a save on any other tab would delete a supplier's fax route.
  // WhatsApp and SMS are managed by the form and rebuilt from its values.
  const preservedEntries: IntegrationEntryInput[] = (existingIntegration ?? [])
    .filter(
      (
        entry
      ): entry is Extract<
        SupplierIntegrationEntry,
        { __typename: 'IntegrationFaxOption' }
      > => entry.__typename === 'IntegrationFaxOption' && Boolean(entry.phone)
    )
    .map((entry) => ({ fax: { type: 'fax' as const, phone: entry.phone } }));

  const integrationEntry: IntegrationEntryInput = {};

  // Build the email section: carry over pdf/xlsx from the existing entry.
  if (orderEmails || messageEmails || complaintEmails) {
    integrationEntry.email = {
      type: 'email',
      // No tab sets this, and the backend still reads it for the bot user's
      // email, so it has to come back exactly as it was stored.
      emailAddress: existingEmail?.emailAddress ?? undefined,
      orderEmails: parseEmailList(orderEmails),
      messageEmails: parseEmailList(messageEmails),
      complaintEmails: parseEmailList(complaintEmails),
      // From the form, not from storage. The Notifications tab shows these as
      // checkboxes now, so the stored value is the old one and reading it back
      // would discard whatever was just ticked.
      pdf: data.notificationOrderPdf,
      xlsx: data.notificationOrderXlsx,
    };
  } else if (existingEmail && !emailFieldsEdited) {
    // No form values, an email entry exists, and nobody touched those fields:
    // preserve it unchanged so a save on a different tab doesn't wipe it.
    //
    // When the fields WERE edited, the empty values are the intent. Preserving
    // here would restore the address the operator just deleted and still report
    // success, so orders would keep going to it.
    integrationEntry.email = {
      type: 'email',
      emailAddress: existingEmail.emailAddress ?? undefined,
      orderEmails: existingEmail.orderEmails ?? [],
      messageEmails: existingEmail.messageEmails ?? [],
      complaintEmails: existingEmail.complaintEmails ?? [],
      // The form seeds these from the stored entry, so an untouched tab sends
      // the same values back and an edited one sends the edit. Reading storage
      // here would silently drop a ticked checkbox.
      pdf: data.notificationOrderPdf,
      xlsx: data.notificationOrderXlsx,
    };
  } else if (existingEmail?.emailAddress) {
    // Every address on screen was cleared, which is the intent, but the entry
    // also holds the deprecated single address no tab shows. Dropping the entry
    // takes that with it, and the backend still derives the distributor's bot
    // user email from it. Keep the entry alive for that address alone; the
    // cleared lists are simply absent, which is what removes them.
    integrationEntry.email = {
      type: 'email',
      emailAddress: existingEmail.emailAddress,
      // Explicitly empty. Omitting them refills each one with `emailAddress`,
      // so orders would carry on going to the bot address the operator cannot
      // see, and the form would say the addresses were removed.
      orderEmails: [],
      messageEmails: [],
      complaintEmails: [],
      // Also the form's. With no address left the flags are inert either way,
      // but reading storage here would make this the one branch that ignores
      // the checkboxes.
      pdf: data.notificationOrderPdf,
      xlsx: data.notificationOrderXlsx,
    };
  }

  if (whatsappPhone) {
    integrationEntry.whatsApp = { type: 'whatsApp', phone: whatsappPhone };
  }

  if (smsPhone) {
    integrationEntry.sms = { type: 'sms', phone: smsPhone };
  }

  // Only emit the merged entry if it has at least one channel populated, then
  // append any preserved (fax) entries.
  const hasMainEntry =
    integrationEntry.email || integrationEntry.whatsApp || integrationEntry.sms;

  if (!hasMainEntry && preservedEntries.length === 0) {
    // Nothing to send. When the last destination was just cleared, that
    // emptiness IS the edit: `undefined` omits the key, the backend merges, and
    // the deleted address stays configured while the form reports success. Send
    // the empty array so the write replaces the stored one.
    return hasEditedNotifications(dirtyFields) ? [] : undefined;
  }

  return [...(hasMainEntry ? [integrationEntry] : []), ...preservedEntries];
};
